import { FC, useMemo } from 'react';
import styled from 'styled-components';
import { colors } from 'theme';
import { SubmissionError } from './SubmissionErrorsDrawerButton';
import { DetailsGrid } from '../declaration-errors-drawer/declaration-errors/declaration-errors.styles';
import { Typography } from 'antd';
import { getFieldName } from 'utils/validationParserUtils';
import useProducts from 'hooks/useProducts';
import useDeclarations from 'hooks/useDeclarations';
import { useLocation, useNavigate } from 'react-router-dom';
import { goToDeclarationField } from 'views/declarations/utils/form-utils';
import useGetDeclarationMapValues from 'hooks/useGetDeclarationMapValues';
import { DeclarationFieldName } from 'views/declarations/common/declarationType';

const SubmissionErrorContainer = styled.ul``;
const SubmissionErrorRow = styled.li``;
const SubmissionErrorCode = styled.p`
    color: ${colors.lightRed};
`;

const { Text } = Typography;

interface Props {
    errors: SubmissionError[] | null | undefined;
}

interface SubmissionErrorProps {
    error: SubmissionError | null;
    onGoToField?: () => void;
}

/**
 * The server reports a field by its path from the declaration root, with array
 * indices in brackets - `cdsDeclaration.cdsDeclarationPayload.previousDocument[0].id`.
 * The form is rooted at the payload and addresses arrays with dots, so the
 * declaration and payload keys come off the front and the brackets are flattened.
 */
const transformField = (field: string | null | undefined, prefixes: (string | undefined)[]) => {
    if (field == null) return null;

    const paths = field.replace(/\[(\d+)\]/g, '.$1').split('.');

    while (paths.length > 1 && prefixes.includes(paths[0])) {
        paths.shift();
    }

    return paths.join('.');
};

const SubmissionErrorField = ({ error }: SubmissionErrorProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    const inViewOnly = useMemo(() => location.pathname.includes('view-only'), [location.pathname]);
    const { products } = useProducts();
    const { declaration } = useDeclarations();
    const { parseForm, itemPath, declarationType, payloadPath } = useGetDeclarationMapValues();

    const prefixes = useMemo(() => [declarationType, payloadPath], [declarationType, payloadPath]);

    /**
     * The segment the item index follows, e.g. `governmentAgencyGoodsItem` in
     * `goodsShipment.governmentAgencyGoodsItem.0.commodity.description`.
     */
    const itemSegment = useMemo(() => itemPath?.split('.').at(-1), [itemPath]);

    const handleGoToField = () => {
        let fieldName = transformField(error?.field, prefixes);

        if (fieldName == null) return;

        // Forms that are not declarations (the ICS2 arrival) report the field by its form path
        // and have no declaration route to navigate to, so the field is only scrolled to.
        if (!declaration) {
            goToDeclarationField(fieldName, 300);
            return;
        }

        const paths = fieldName.split('.');
        const itemPosition = itemSegment ? paths.indexOf(itemSegment) : -1;
        const reportedIndex = itemPosition === -1 ? NaN : Number(paths[itemPosition + 1]);

        if (!isNaN(reportedIndex)) {
            /**
             * Revenue numbers its items from one, the CDS service reports the
             * array index, so only the former needs shifting.
             */
            const productIndex = declarationType === DeclarationFieldName.UK ? reportedIndex : reportedIndex - 1;
            const product = products?.list.at(productIndex);

            fieldName = paths.slice(itemPosition + 2).join('.');

            if (product?.id) {
                navigate(`/declarations/${declaration?.id}${inViewOnly ? '/view-only' : ''}/products/${product.id}`);
            }
        } else if (location.pathname.split('/').at(-1) !== declaration?.id) {
            navigate(`/declarations/${declaration?.id}${inViewOnly ? '/view-only' : ''}`);
        }
        goToDeclarationField(fieldName, 300);
    };

    const uiField = useMemo(() => {
        const field = transformField(error?.field, prefixes);
        const _uiField = field && parseForm && getFieldName(parseForm, field, itemSegment);
        if (field && (!_uiField || _uiField === 'Unknown card - Unknown field')) {
            return field;
        }
        if (!field) {
            return error?.field;
        }
        return _uiField;
    }, [error?.field, itemSegment, parseForm, prefixes]);

    return (
        <>
            <DetailsGrid style={{ marginTop: '2rem' }}>
                <Text strong>Field:</Text>
                {uiField ? (
                    <Typography.Link onClick={handleGoToField}>{uiField}</Typography.Link>
                ) : (
                    <Text>{uiField || 'n/a'}</Text>
                )}
            </DetailsGrid>
        </>
    );
};

const SubmissionErrorsList: FC<Props> = ({ errors }) => {
    return (
        <SubmissionErrorContainer>
            {errors?.map((error) => (
                <SubmissionErrorRow>
                    {error.code || <SubmissionErrorCode>{error.code}</SubmissionErrorCode>}
                    <SubmissionErrorField error={error}></SubmissionErrorField>
                    <span>{error?.message}</span>
                </SubmissionErrorRow>
            ))}
        </SubmissionErrorContainer>
    );
};

export default SubmissionErrorsList;
